import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { consentText } from '@mobi/account/Areas/SignUp/Components/Message'
import { DocumentPermission } from '@mobi/account/Areas/SignUp/Components/Verification'
import {
  ALPHANUMERIC_REGEX,
  getRequiredValidationMessage,
  isValidName,
  routes,
} from '@mobi/account/Areas/SignUp/Utils'
import { NameInput } from '@mobi/account/Common'
import { signupTracker } from '@mobi/account/Common/Analytics'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { StepGroup } from '@mobi/component-library/Base/StepGroup'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { spacing } from '@mobi/component-library/Foundations'
import { AlertBox } from '@mobi/component-library/Overlays/AlertBox'
import { DropdownInput } from '@mobi/component-library/Selection&Input/DropdownInput'
import { Input } from '@mobi/component-library/Selection&Input/Inputs'
import { statesInAustralia } from '@mobi/settings/statesInAustralia'
import { useApplication, useApplicationSubmit } from '../../Hooks'
import {
  getSignUpState,
  setIsApplicationPendingReview,
  setNumberOfAttemptsToVerify,
  setSignupApplication,
  setSignupVerification,
} from '../../Store'
import cardNumberInfoImage from '../../assets/card-number-info.png'
import { SignupError } from '../SignupError'
import { DocumentBodyStyled, FormLayout } from './Layout.styled'

export const DriversLicence = () => {
  const [showCardNumberInfoModal, setShowCardNumberInfoModal] = useState(false)
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)

  const [state, setState] = React.useState<string | null>('')
  const [number, setNumber] = React.useState('')
  const [cardNumber, setCardNumber] = React.useState('')

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const signUpState = useSelector(getSignUpState)
  const application = signUpState.signupApplication
  const verification = signUpState.signupVerification
  const { applicationSave, applicationSaveStatus } = useApplication({
    setValidationMessages,
    setShowErrorPopup,
  })
  const { applicationSubmit, applicationSubmitStatus } = useApplicationSubmit(
    application.applicationReferenceNumber
  )

  const isBusy = applicationSaveStatus === 'pending' || applicationSubmitStatus === 'pending'

  const allowToProceed =
    givePermission &&
    !!state &&
    !!number &&
    !!cardNumber &&
    isValidName(application.firstName) &&
    isValidName(application.middleName, true) &&
    isValidName(application.surname)

  const onSubmit = () => {
    if (!allowToProceed) {
      return
    }

    dispatch(setNumberOfAttemptsToVerify())

    applicationSave(
      { driverLicenceDetails: { number, state, cardNumber } },
      {
        onSuccess: res => {
          if (res.duplicateExists) {
            return
          }

          applicationSubmit(
            { termsAndConditionsText: consentText, termsAndConditionsConsent: givePermission },
            {
              onSuccess: res => {
                dispatch(setSignupApplication({ ...application, enquiryId: res.enquiryId }))
                dispatch(setIsApplicationPendingReview(res.pendingReview))
                dispatch(
                  setSignupVerification({
                    ...verification,
                    isVerified: res.isVerified,
                    unVerifiedReason: res.unverifiedReason ?? '',
                  })
                )
                if (res.isVerified && !res.pendingReview) {
                  signupTracker({
                    interaction_type: 'ID Verification Success',
                    signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
                    attemptNumber: signUpState.numberOfAttemptsToVerify.toString(),
                    optionSelected: 'Licence',
                  })
                  navigate(routes.password)
                  return
                }
                navigate(routes.signUpUnsuccessful)
              },
              onError: () => {
                setShowErrorPopup(true)
              },
            }
          )
        },
      }
    )
  }

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  if (!(application.applicationReferenceNumber > 0)) {
    return null
  }

  return (
    <>
      <FormLayout>
        <StepGroup currentStep={3} totalSteps={5} />
        <AccountHeader
          alignItems='start'
          title='Enter your Driver’s Licence details'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your details as they appear on your ID document.
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <DocumentBodyStyled>
          <NameInput
            firstName={application.firstName}
            middleName={application.middleName}
            lastName={application.surname}
            setFirstName={firstName =>
              dispatch(setSignupApplication({ ...application, firstName }))
            }
            setMiddleName={middleName =>
              dispatch(setSignupApplication({ ...application, middleName }))
            }
            setLastName={surname => dispatch(setSignupApplication({ ...application, surname }))}
            isDisabled={isBusy}
          />
          <DropdownInput
            name='state'
            label='State'
            placeholder='Details must match your ID'
            value={state || ''}
            isDisabled={isBusy}
            onChange={event => {
              setState(event.target.value || '')
              resetFieldValidation('state')
            }}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                state: getRequiredValidationMessage(state),
              }))
            }}
            isInvalid={!!validationMessages.state}
            helpText={validationMessages.state}
          >
            {statesInAustralia.map(state => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </DropdownInput>
          <Input
            name='licenceNumber'
            label={`Driver's licence number`}
            placeholder='Details must match your ID'
            isDisabled={isBusy}
            value={number}
            onChange={e => setNumber(e.target.value.replace(ALPHANUMERIC_REGEX, ''))}
            onFocus={() => resetFieldValidation('licenceNumber')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                licenceNumber: getRequiredValidationMessage(number),
              }))
            }}
            isInvalid={!!validationMessages.licenceNumber}
            helpText={validationMessages.licenceNumber}
          />
          <Input
            name='cardNumber'
            label={`Driver's licence card number`}
            placeholder='Details must match your ID'
            isDisabled={isBusy}
            value={cardNumber}
            onChange={e => setCardNumber(e.target.value.replace(ALPHANUMERIC_REGEX, ''))}
            onFocus={() => resetFieldValidation('cardNumber')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                cardNumber: getRequiredValidationMessage(cardNumber),
              }))
            }}
            isInvalid={!!validationMessages.cardNumber}
            helpText={validationMessages.cardNumber}
            suffix={
              <Icon
                name='LineInfoCircle'
                size='2rem'
                onClick={() => {
                  signupTracker({
                    interaction_type: 'Drivers License Info',
                    signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
                  })
                  setShowCardNumberInfoModal(true)
                }}
                data-testid='drivers-licence-info'
              />
            }
          />
          <div style={{ paddingTop: spacing.smx1 }}>
            <DocumentPermission
              isBusy={isBusy}
              onChange={e => setGivePermission(e.target.checked)}
            />
          </div>
        </DocumentBodyStyled>
        <footer>
          <Button
            isFullWidth
            disabled={!allowToProceed}
            isLoading={isBusy}
            onClick={() => onSubmit()}
          >
            Continue
          </Button>
        </footer>
        <AlertBox
          title='Driver’s Licence Card Number'
          text="A 'card number' is now a mandatory verification field for Australian driver's licences. Depending on your state, this may be found on either the front or back of the card."
          confirmText='Close'
          confirmColor='primary'
          isOpen={showCardNumberInfoModal}
          onConfirm={() => setShowCardNumberInfoModal(false)}
          onCancel={() => setShowCardNumberInfoModal(false)}
          imageCover={<img src={cardNumberInfoImage} alt='Card Number Info' />}
          alignLeft
          maxWidth='35rem'
        />
      </FormLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}
